// One playback service shared by every chat audio bubble. On iOS we route
// through the native AVAudioPlayer plugin so we're not subject to WKWebView's
// per-element user-gesture lock. Everywhere else we use a single shared
// HTML5 <audio> element, primed once on first user gesture so subsequent
// programmatic plays don't get blocked.

import { reactive, computed, watch } from "vue";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { replyLagAnchors } from "@/store/pendingTutorResponses.js";
import {
  effectivePlaybackRate,
  playbackRateAvailable,
  playbackRateState,
} from "@/scripts/playbackRate.js";
import { usePlaybackEvents } from "@/composables/usePlaybackEvents.js";
import {
  clipCacheState,
  clipObjectUrl,
  evictUnheldClips,
  getClip,
  peekClip,
  registerPlayerHolds,
} from "@/scripts/clipCache.js";
import { recordingState } from "@/store/recordingState.js";
import { registerSliceController, sliceState } from "@/scripts/slicePlayback.js";

const NativeAudio = registerPlugin("NativeAudioPlayer");
const useNative = Capacitor.getPlatform() === "ios";

export const playbackState = reactive({
  // null until any clip has been played; otherwise the audioId of the clip
  // currently held by the player. Cleared back to null on natural end so the
  // next play() on the same id restarts from the beginning.
  currentAudioId: null,
  isPlaying: false,
  currentTime: 0,
  duration: 0,
});

// The rate actually applied to tutor audio (see src/scripts/playbackRate.js):
// the speed chip's preference while the chip can be shown, and 1 otherwise.
// The pill's time labels, the reveal pace and this service's own segment
// watchdog all read this rather than the stored preference.
export { effectivePlaybackRate, playbackRateAvailable };

// The phrase slice player's state (see "Phrase slices" below) and the clip
// cache's fetch, re-exported so callers that already import the service can
// reach them here.
export { sliceState, getClip };

// What the clip the shared player holds is playing at. `followsChip` is true
// for a clip whose caller passed a rate, which only a tutor pill in a lesson or
// a freestyle chat does (and the segment sessions the chat marks the same
// way): such a clip changes speed with the chip mid-play. Every other clip was
// started at 1x and stays there. Reactive because the listening analytics
// below derive from it.
const currentClip = reactive({ followsChip: false, rate: 1 });

// ---------- Native (iOS) path ----------
//
// The Swift plugin only emits one event during a normal playback (audioEnded
// when the clip finishes). We track currentTime locally with
// requestAnimationFrame so the bridge stays silent. AVAudioPlayer plays at a
// known rate, so a JS media-time clock anchored at every play, resume, seek,
// pause and rate change is accurate enough for a chat progress bar.
//
// Every one of those sites goes through anchorNativeClock(mediaTime, rate),
// which records the wall-clock moment media time zero would have been at that
// rate. The tick then computes media time as elapsed wall time multiplied by
// the rate. A pause snapshots the media time it stopped at, and a resume
// re-anchors from that snapshot, so no pause time is ever accumulated and a
// seek made while paused is simply the new snapshot. The rate reaches this
// clock only once the plugin has reported rate support (nativeRateSupported),
// so a build with this JavaScript and an older plugin keeps a 1x clock against
// its 1x audio.

let nativeClockAnchor = 0; // performance.now() at which media time 0 would have been
// Bumped by everything that moves or re-times the clock: every anchor, every
// seek (including one made while paused, which only records the position) and
// every pause. An answer from the plugin about the position is applied only if
// nothing did so while it was on its way.
let nativeClockGeneration = 0;
// The token of the load that installed the player the plugin holds, so a
// setRate can name it the way the targeted pause does.
let nativeInstalledToken = null;
let nativeClockRate = 1;
let nativeRafId = null;

function anchorNativeClock(mediaTime, rate) {
  nativeClockGeneration++;
  nativeClockRate = rate > 0 && isFinite(rate) ? rate : 1;
  const time = isFinite(mediaTime) ? mediaTime : 0;
  nativeClockAnchor = performance.now() - (time * 1000) / nativeClockRate;
}

function nativeClockMediaTime() {
  const elapsed = ((performance.now() - nativeClockAnchor) / 1000) * nativeClockRate;
  const dur = playbackState.duration;
  return dur > 0 ? Math.min(elapsed, dur) : elapsed;
}

function tickNativeProgress() {
  nativeRafId = null;
  if (!playbackState.isPlaying) return;
  const elapsed = ((performance.now() - nativeClockAnchor) / 1000) * nativeClockRate;
  const dur = playbackState.duration;
  playbackState.currentTime = dur > 0 ? Math.min(elapsed, dur) : elapsed;
  if (dur > 0 && elapsed >= dur) {
    // Hold at the end; the audioEnded listener will reset state shortly.
    return;
  }
  nativeRafId = requestAnimationFrame(tickNativeProgress);
}

function startNativeProgress() {
  if (nativeRafId == null) {
    nativeRafId = requestAnimationFrame(tickNativeProgress);
  }
}

function cancelNativeProgress() {
  if (nativeRafId != null) {
    cancelAnimationFrame(nativeRafId);
    nativeRafId = null;
  }
}

// Whether the installed plugin accepts a rate. Only a plugin that reports it
// through getCapabilities is sent one: an older plugin rejects the unknown
// method (Capacitor refuses calls to unregistered methods), and then nothing
// here changes from today's 1x behavior.
let nativeRateSupported = false;

function requestNativeCapabilities() {
  if (!NativeAudio || typeof NativeAudio.getCapabilities !== "function") return;
  let pending;
  try {
    pending = NativeAudio.getCapabilities();
  } catch {
    return;
  }
  Promise.resolve(pending)
    .then((capabilities) => {
      nativeRateSupported = capabilities?.rate === true;
      // Phrase slices need both: playRange, and the rate the chip applies.
      nativeSliceSupported = capabilities?.rate === true && capabilities?.range === true;
      // The chip appears only when the plugin can also play a phrase range,
      // because the phrase controls that follow it need both.
      playbackRateState.platformSupported =
        capabilities?.rate === true && capabilities?.range === true;
    })
    .catch(() => {
      // An older plugin: no rate, no chip, a 1x clock.
    });
}

if (useNative) {
  requestNativeCapabilities();
} else {
  // Every browser the app supports implements playbackRate with pitch
  // correction, so web and Android can show the chip at once.
  playbackRateState.platformSupported = true;
}

if (useNative) {
  NativeAudio.addListener("audioEnded", ({ id, successfully }) => {
    if (playbackState.currentAudioId === id) {
      cancelNativeProgress();
      playbackState.isPlaying = false;
      playbackState.currentTime = playbackState.duration;
      // Clear so the next play() on the same id restarts from 0 instead of
      // hitting a finished AVAudioPlayer that resume() can't restart.
      playbackState.currentAudioId = null;
    }
    // AVAudioPlayer reports a decode failure partway through a clip through
    // the same didFinishPlaying callback, with successfully = false. That is a
    // failure of the clip, not a natural end, so a segment session must not
    // chain past it. Absent (a plugin build that predates the flag) means a
    // natural end, which is what the event always meant before.
    if (successfully === false) {
      handleSegmentPlaybackStalled(id);
      return;
    }
    // Natural completion, which is the ONLY event a segment session may chain
    // on. The id comes from the plugin rather than from module state, which is
    // what the session compares against its own record.
    handleSegmentAudioEnded(id);
  });
  // The slice player tells us every way a slice ended except stopRange: its
  // timer firing at the span's end ("timer"), an audio interruption, a failed
  // play, or being preempted by the main player. Only the slice this event
  // names by its token may act on it.
  NativeAudio.addListener("rangeEnded", (event) => handleNativeRangeEnded(event));
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      const comma = dataUrl.indexOf(",");
      resolve(comma >= 0 ? dataUrl.slice(comma + 1) : "");
    };
    reader.onerror = () => reject(reader.error || new Error("FileReader failed"));
    reader.readAsDataURL(blob);
  });
}

// ---------- HTML5 (web/Android) path ----------

const SILENT_MP3 =
  "data:audio/mp3;base64,SUQzBAAAAAABEVRYWFgAAAAtAAADY29tbWVudABCaWdTb3VuZEJhbmsuY29tIC8gTGFTb25vdGhlcXVlLm9yZwBURU5DAAAAHQAAA1N3aXRjaCBQbHVzIMKpIE5DSCBTb2Z0d2FyZQBUSVQyAAAABgAAAzIyMzUAVFNTRQAAAA8AAANMYXZmNTcuODMuMTAwAAAAAAAAAAAAAAD/80DEAAAAA0gAAAAATEFNRTMuMTAwVVVVVVVVVVVVVUxBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV";

// The phrase slice player's element (see "Phrase slices" below). Created here,
// BEFORE the shared element, on purpose: the service's test harnesses find the
// shared element as the most recently constructed Audio.
const sliceAudio = useNative ? null : new Audio();
if (sliceAudio) sliceAudio.preload = "auto";

const audio = useNative ? null : new Audio();
if (audio) audio.preload = "auto";

// URL.createObjectURL() pins the underlying blob in memory until it's
// revoked. We revoke before swapping in a new src and on natural end so the
// blobs from past chat messages don't accumulate over a long session.
function revokeCurrentObjectUrl() {
  if (audio && typeof audio.src === "string" && audio.src.startsWith("blob:")) {
    URL.revokeObjectURL(audio.src);
  }
}

// Both rates, every time. The element resets playbackRate to
// defaultPlaybackRate whenever its src is swapped, which this service does per
// clip, so setting only playbackRate would lose the rate at the next clip.
// Pitch preservation is the browsers' default, and it is set explicitly (with
// the WebKit spelling) so the time-stretch never turns into a pitch shift.
function applyElementRate(rate) {
  if (!audio) return;
  try {
    audio.defaultPlaybackRate = rate;
    audio.playbackRate = rate;
    audio.preservesPitch = true;
    audio.webkitPreservesPitch = true;
  } catch (err) {
    console.warn("Could not set the playback rate:", err);
  }
}

if (audio) {
  audio.addEventListener("play", () => {
    playbackState.isPlaying = true;
  });
  audio.addEventListener("pause", () => {
    playbackState.isPlaying = false;
  });
  audio.addEventListener("ended", () => {
    // Read the id BEFORE the reset below clears it. A segment session cannot
    // recover it afterwards, which is why the session keeps its own record and
    // this handler hands the finished id over explicitly.
    const endedAudioId = playbackState.currentAudioId;
    playbackState.isPlaying = false;
    playbackState.currentAudioId = null;
    revokeCurrentObjectUrl();
    // Deliberately after the reset and the revoke: chaining loads a new source
    // into this same element, and doing it first would have the cleanup above
    // undo the clip that just started.
    handleSegmentAudioEnded(endedAudioId);
  });
  audio.addEventListener("error", () => {
    // A media error raised AFTER play() resolved - a truncated body, a decode
    // failure partway through the blob - stops playback and never fires
    // `ended`. Without this a segment session would wait for a chain step that
    // can never come.
    const failedAudioId = playbackState.currentAudioId;
    playbackState.isPlaying = false;
    handleSegmentPlaybackStalled(failedAudioId);
  });
  audio.addEventListener("timeupdate", () => {
    playbackState.currentTime = audio.currentTime;
    // The web path's evidence that sound is actually coming out: the media
    // timeline has moved past zero on an unmuted, unpaused element. play()
    // resolving is not that evidence, because it resolves before a single frame
    // has been decoded. The load algorithm's own timeupdate reports zero and the
    // muted priming clip is excluded, so neither can pass for audible progress.
    if (!audio.paused && !audio.muted && audio.currentTime > 0) {
      noteAudibleProgress(playbackState.currentAudioId);
    }
  });
  audio.addEventListener("loadedmetadata", () => {
    playbackState.duration = isFinite(audio.duration) ? audio.duration : 0;
  });
  audio.addEventListener("durationchange", () => {
    playbackState.duration = isFinite(audio.duration) ? audio.duration : 0;
  });
}

let primed = false;
let primingPromise = null;

function prime() {
  if (!audio) return Promise.resolve();
  if (primed) return Promise.resolve();
  if (primingPromise) return primingPromise;
  primingPromise = (async () => {
    audio.src = SILENT_MP3;
    audio.muted = true;
    try {
      await audio.play();
      audio.pause();
      audio.currentTime = 0;
      primed = true;
    } catch (err) {
      console.warn("Audio prime failed:", err);
    } finally {
      audio.muted = false;
    }
  })();
  return primingPromise;
}

if (!useNative) {
  const onceUnlock = () => {
    prime();
    primeSliceElement();
    document.removeEventListener("touchstart", onceUnlock, { capture: true });
    document.removeEventListener("click", onceUnlock, { capture: true });
    document.removeEventListener("keydown", onceUnlock, { capture: true });
  };
  document.addEventListener("touchstart", onceUnlock, { once: true, capture: true });
  document.addEventListener("click", onceUnlock, { once: true, capture: true });
  document.addEventListener("keydown", onceUnlock, { once: true, capture: true });
}

// ---------- Public API ----------

// Total by construction: the clamp alone passes NaN straight through, and a
// NaN currentTime throws in real browsers. Non-finite means "start at zero".
function clampRatio(ratio) {
  return isFinite(ratio) ? Math.max(0, Math.min(1, ratio)) : 0;
}

// The play() request whose load was most recently sent to the iOS plugin. Its
// token is the one the plugin will let start when the decode lands; see
// stopMainRequestsOnTheBridge.
let latestNativeLoad = null;

// Bumped by every play() so a call that resumes from an await after a newer
// one started can bail instead of seeking to its own stale position. Same
// hazard and same shape as lastRequestedId in NativeAudioPlayerPlugin.swift.
let playGeneration = 0;

// Every play() that has not finished starting its clip, one record per call:
// { generation, audioId, cancelled }. A call is stale - and gives up after its
// next await - when a newer generation exists OR when its own record was
// cancelled. The generation cancels everything at once ("stop talking now");
// the per-record flag cancels one clip's load without touching anyone else's,
// which is what "stop THIS clip" and "this segment session is over" need.
const inFlightPlays = new Set();

function isStalePlay(request) {
  return request.cancelled || request.generation !== playGeneration;
}

function cancelPlaysOf(audioId) {
  if (!audioId) return;
  inFlightPlays.forEach((request) => {
    if (request.audioId === audioId) request.cancelled = true;
  });
}

// The reply-lag audio-start stamp, exposed as a hook rather than inlined so
// more than one playback path can report the same moment. `play()` below
// stamps the whole-file path; a segment session stamps segment 0 itself, on
// audible progress (see noteAudibleProgress).
//
// lagContext identifies WHICH message is starting: { dialogId, messageKey }.
// The shared player is keyed by audioId, and an audioId is not a message, so
// the caller has to supply it. A caller with no context (a message from
// history, a preview clip) passes nothing and stamps nothing.
//
// The anchor store applies every rule about whether a stamp is owed at all —
// there must be an anchor bound to that message, it must not have expired, and
// it must not already have been consumed by an earlier play. So calling this
// on a manual replay is harmless by construction.
export function stampReplyAudioStart(lagContext) {
  if (!lagContext || !lagContext.dialogId || !lagContext.messageKey) return;
  replyLagAnchors.recordAudioStart(lagContext.dialogId, lagContext.messageKey);
}

// startRatio carries a scrub position as a fraction of the clip rather than a
// timestamp, because the gesture that produces it (an x-offset across the
// waveform) has no duration to work with — only whoever loads the audio does.
//
// null means "don't seek"; a number in 0..1 means "seek there". 0 cannot carry
// both: pressing play on a paused clip must resume where it left off (null),
// while scrubbing to the very start is a real request for position 0.
//
// lagContext is the reply-lag stamp's { dialogId, messageKey }, or null on a
// call that is not a chat reply. It is stamped only once playback has actually
// begun, so a refused play (blocked autoplay) reports nothing.
//
// rate is the speed to play at, or null. Only a tutor pill in a lesson or a
// freestyle chat passes one (the effective rate from playbackRate.js), and such
// a clip then follows the speed chip if it changes mid-play. null means 1x and
// is what every other caller passes by passing nothing: the learner's own
// recording, the admin viewer and Daily Play. It is the fifth argument, after
// lagContext, because tests/unit/replyLagWiring.test.js pins that every play()
// in AudioPlayer passes the lag context as its fourth.
//
// Resolves to true only when playback actually started, and false when the
// clip was refused (blocked autoplay, a decode failure, a native bridge error)
// or when a newer call superseded this one. A segment session needs that
// answer: a clip that never started fires no natural-end event, so a session
// that assumed success would wait for a chain step that can never come.
export async function play(audioId, blob, startRatio = null, lagContext = null, rate = null) {
  // Every main play, a new reply's autoplay included, silences a phrase slice
  // and ends its loop first, so a loop can never sound over a new reply.
  stopActiveSlice();
  // Any clip other than a session's own current segment is something else
  // taking the shared player, which means "stop talking now" for every segment
  // session -- including one that has not started a clip yet and therefore
  // holds no claim on the player. The session did not fail, and the learner did
  // not necessarily silence it (the clip may be a stitched file autoplaying):
  // it was superseded.
  endSegmentSessionsExcept(audioId, "superseded");
  const request = { generation: ++playGeneration, audioId, cancelled: false };
  inFlightPlays.add(request);
  try {
    return await startClip(request, blob, startRatio, lagContext, rate);
  } finally {
    inFlightPlays.delete(request);
  }
}

// The rate a play() call asked for, as the number to apply.
function appliedRateFor(rate) {
  return typeof rate === "number" && rate > 0 && isFinite(rate) ? rate : 1;
}

// Records what the clip now starting plays at. Called just before the start,
// after every staleness check, so the listening analytics see the clip's own
// rate the moment isPlaying turns true.
function noteCurrentClipRate(rate, startRate) {
  currentClip.followsChip = rate !== null && rate !== undefined;
  currentClip.rate = startRate;
}

// Bumped by every change to the effective rate. A play() records it on entry,
// and when it has changed by the time the clip actually starts (the chip moved,
// or the flag turned off, during the load), a chip-following clip starts at the
// effective rate in force then rather than the one its caller passed.
let effectiveRateEpoch = 0;

// Asks the plugin for a new rate on the player it holds, naming that player by
// the clip's id and the token of the load that installed it: a newer clip can
// replace the player on the plugin's main queue while this call crosses the
// bridge, and the plugin applies the rate only to the player it names, so a
// learner's own recording can never be slowed by a chip change meant for the
// tutor clip before it.
//
// If the plugin refuses, the audio keeps its old rate while the chip, the
// labels and the watchdog describe the new one. The clip is stopped when the
// old rate is no longer the effective one (the chip moved, or the flag turned
// off): a clip still sounding at a speed nothing on screen shows is the one
// state the effective-rate contract rules out. Pressing play resumes it at the
// effective rate. The clock is then put back where the audio really is, which
// only the plugin knows: a seek, a pause or a resume may have happened while
// the request was in flight, so nothing recorded when it was sent still
// describes the position.
// Counts setRate requests, so the handler for a refused request can tell that
// a newer request has replaced it; the rate alone does not identify a request,
// because cycling the chip can ask for the same rate twice.
let nativeRateRequestSeq = 0;

function setNativeRate(nextRate, previousRate) {
  const clipId = playbackState.currentAudioId;
  const token = nativeInstalledToken;
  const requestSeq = ++nativeRateRequestSeq;
  let request;
  try {
    request = NativeAudio.setRate({ rate: nextRate, id: clipId, token });
  } catch (err) {
    request = Promise.reject(err);
  }
  Promise.resolve(request).catch(async (err) => {
    console.warn("The native player refused the new playback rate:", err);
    const stillThisPlayer = () =>
      playbackState.currentAudioId === clipId && nativeInstalledToken === token;
    // A later request, or another clip, has superseded this one.
    if (requestSeq !== nativeRateRequestSeq) return;
    if (!stillThisPlayer() || currentClip.rate !== nextRate) return;
    currentClip.rate = previousRate;
    const mustStop = previousRate !== effectivePlaybackRate.value;
    if (mustStop && playbackState.isPlaying) stopClipAfterRateRefusal(clipId);
    const clockGenerationAtAsk = nativeClockGeneration;
    let status = null;
    if (typeof NativeAudio.getStatus === "function") {
      try {
        status = await NativeAudio.getStatus();
      } catch {
        status = null;
      }
    }
    if (!stillThisPlayer()) return;
    // A seek, a resume, a pause or another rate change while the answer was
    // on its way moved the clock on purpose, and the answer is older than it.
    if (nativeClockGeneration !== clockGenerationAtAsk) return;
    const position = Number(status && status.currentTime);
    if (status && status.currentId === clipId && isFinite(position)) {
      playbackState.currentTime = position;
      anchorNativeClock(position, previousRate);
    }
    if (!mustStop) rearmSegmentWatchdogForRate();
  });
}

// A plugin failure, not a learner action: a segment session playing the clip
// ends as "failed" rather than "silencedByLearner", and the clip's own pending
// loads are cancelled before the player is stopped, as "stop THIS clip" does.
function stopClipAfterRateRefusal(clipId) {
  segmentSessions.forEach((session) => {
    if (session.playingAudioId === clipId) endSegmentSession(session, "failed");
  });
  cancelPlaysOf(clipId);
  stopSharedPlayer(clipId);
}

async function startClip(request, blob, startRatio, lagContext, rate) {
  const audioId = request.audioId;
  const appliedRate = appliedRateFor(rate);
  const epochAtStart = effectiveRateEpoch;
  // The rate to start at, read at the moment of starting: the passed rate,
  // unless the effective rate changed while this call was loading.
  const startRateNow = () =>
    rate !== null && rate !== undefined && effectiveRateEpoch !== epochAtStart
      ? effectivePlaybackRate.value
      : appliedRate;
  if (useNative) {
    if (playbackState.currentAudioId === audioId) {
      // Seek before the isPlaying early-return, or scrubbing a clip that is
      // already playing would return without moving it.
      if (startRatio !== null && playbackState.duration > 0) {
        seek(playbackState.duration * clampRatio(startRatio));
      }
      if (playbackState.isPlaying) return true;
      try {
        // An older plugin is called exactly as before, with no argument.
        request.nativeResumeSent = true;
        if (nativeRateSupported) {
          await NativeAudio.resume({ rate: appliedRate });
        } else {
          await NativeAudio.resume();
        }
        if (isStalePlay(request)) {
          // A newer play takes the plugin's player itself. A request that was
          // cancelled on its own (cancelPendingPlaysOf: the pill that asked is
          // gone, or its span navigation was refused) has no successor, and
          // the plugin has just resumed this clip, so it is paused here,
          // targeted at the player this clip installed.
          if (
            request.cancelled &&
            request.generation === playGeneration &&
            playbackState.currentAudioId === audioId
          ) {
            NativeAudio.pause({ id: audioId, token: nativeInstalledToken }).catch(() => {});
            cancelNativeProgress();
            playbackState.isPlaying = false;
          }
          return false;
        }
        // A load this service had already given up on can land while the
        // resume is crossing the bridge and replace the plugin's player (the
        // stale-result branch below clears currentAudioId when that happens).
        // What the plugin just resumed is then NOT this clip, so this call must
        // not report it playing under this clip's controls; it falls through
        // to a fresh load of the right bytes instead.
        if (playbackState.currentAudioId === audioId) {
          // Re-anchor from the media time the pause (or a seek made while
          // paused) left behind, at the rate the clip resumes at.
          const startRate = startRateNow();
          // The iOS clock follows the rate only when the plugin applies it.
          anchorNativeClock(playbackState.currentTime, nativeRateSupported ? startRate : 1);
          noteCurrentClipRate(rate, startRate);
          if (nativeRateSupported && startRate !== appliedRate) {
            setNativeRate(startRate, appliedRate);
          }
          playbackState.isPlaying = true;
          startNativeProgress();
          stampReplyAudioStart(lagContext);
          return true;
        }
      } catch (err) {
        // Fall through to a fresh load. AVAudioPlayer.play() after a finished
        // clip can fail to resume; reloading from bytes handles that case.
        console.warn("Native audio resume failed, reloading:", err);
      }
    }
    try {
      const data = await blobToBase64(blob);
      if (isStalePlay(request)) return false;
      // Duration comes back *from* this call, so JS can't convert the ratio to
      // seconds first — the ratio crosses the bridge and Swift converts it
      // against AVAudioPlayer.duration, which it knows at decode time.
      // Token, not id: two scrubs of the same clip cross the bridge with the
      // same id, so the plugin's stale-decode guard needs something that
      // actually differs between them or it lets both through.
      const options = { id: audioId, data, token: String(request.generation) };
      if (startRatio !== null) options.startRatio = clampRatio(startRatio);
      if (nativeRateSupported) options.rate = appliedRate;
      request.nativeLoadSent = true;
      latestNativeLoad = request;
      const result = await NativeAudio.play(options);
      // The plugin keeps the bytes of the two most recent clips it was sent,
      // which a phrase slice of this clip can then play without resending.
      if (!result || result.superseded !== true) noteNativeClipData(audioId);
      if (isStalePlay(request)) {
        // This request is stale. Whether anything is audible because of it
        // depends on which side won: the plugin drops a request that a newer
        // token superseded and answers `superseded: true` without ever
        // starting a player, and pausing on that answer would silence the
        // NEWER clip that is legitimately playing. Only a decode that the
        // plugin actually started needs stopping here, because the generation
        // check alone would leave it audible over a recording or over whatever
        // screen the learner moved to.
        if (!result || result.superseded !== true) {
          // Targeted at THIS request. A newer load can register, decode and
          // replace this one on the plugin's main queue while this answer is
          // on its way to JS, and an untargeted pause would then silence that
          // newer clip while its controls say it is playing. The id alone is
          // not enough: a newer request for the SAME clip (two scrubs) has the
          // same id. The token is what this request installed, so the plugin
          // pauses only if the player it holds is still this request's.
          NativeAudio.pause({ id: audioId, token: options.token }).catch(() => {});
          cancelNativeProgress();
          playbackState.isPlaying = false;
          // The plugin's pause() does not cancel a decode already under way,
          // so the plugin now holds THIS clip, paused, and no longer holds
          // whatever currentAudioId names. Leaving the old id there would send
          // the next play() of that clip down the resume branch above, which
          // would audibly resume this clip under the other clip's controls.
          // Clearing it makes the next play() of any clip load fresh. No newer
          // load can have been applied before this answer arrived: the plugin
          // applies requests in order on its main queue, and a request
          // registered before this decode landed would have made this one
          // `superseded` instead.
          playbackState.currentAudioId = null;
          // Nothing this service tracks is playing now; the next play()
          // anchors the clock afresh.
          anchorNativeClock(0, 1);
        }
        return false;
      }
      playbackState.currentAudioId = audioId;
      nativeInstalledToken = options.token;
      playbackState.duration =
        result && typeof result.duration === "number" && isFinite(result.duration)
          ? result.duration
          : 0;
      // Read duration back off playbackState rather than result: it carries
      // the isFinite guard, so an unreadable container starts from the
      // beginning instead of anchoring the clock at NaN.
      const startTime =
        startRatio === null ? 0 : playbackState.duration * clampRatio(startRatio);
      playbackState.currentTime = startTime;
      // The player was installed at the rate sent; if the chip moved while
      // the clip crossed the bridge, move the new player to the current rate.
      const startRate = startRateNow();
      noteCurrentClipRate(rate, startRate);
      anchorNativeClock(startTime, nativeRateSupported ? startRate : 1);
      if (nativeRateSupported && startRate !== appliedRate) {
        setNativeRate(startRate, appliedRate);
      }
      playbackState.isPlaying = true;
      startNativeProgress();
      stampReplyAudioStart(lagContext);
      return true;
    } catch (err) {
      console.warn("Native audio play failed:", err);
    }
    return false;
  }

  await prime();
  if (isStalePlay(request)) return false;
  if (playbackState.currentAudioId !== audioId) {
    // Swapping the source silences the clip that was playing, and the element
    // does not always say so with a pause event. Nothing follows the chip
    // until the new clip starts, so the listening analytics do not count its
    // load as listening; noteCurrentClipRate sets it again just before play.
    currentClip.followsChip = false;
    revokeCurrentObjectUrl();
    audio.src = blob instanceof Blob ? URL.createObjectURL(blob) : blob;
    audio.currentTime = 0;
    playbackState.currentAudioId = audioId;
    playbackState.currentTime = 0;
    playbackState.duration = 0;
  }
  // Outside the branch above on purpose. currentAudioId is claimed before the
  // load finishes, so a second play() on the same clip arrives here seeing a
  // "current" clip whose duration is still 0 and would seek against it.
  if (audio.readyState < 3) {
    await new Promise((resolve) => {
      const settle = () => {
        audio.removeEventListener("canplay", settle);
        audio.removeEventListener("error", settle);
        resolve();
      };
      audio.addEventListener("canplay", settle, { once: true });
      audio.addEventListener("error", settle, { once: true });
    });
    if (isStalePlay(request)) return false;
  }
  // Seek before play() so an unplayed clip opens *at* the scrub position
  // rather than at zero followed by an audible jump. playbackState.duration is
  // known by now (loadedmetadata has fired) and carries the isFinite guard
  // that audio.duration does not.
  if (startRatio !== null) {
    const startTime = playbackState.duration * clampRatio(startRatio);
    audio.currentTime = startTime;
    // Mirror into the store: play() zeroed currentTime for the new source and
    // nothing refreshes it until the next timeupdate, so the waveform would
    // snap to the start and then jump back a tick later.
    playbackState.currentTime = startTime;
  }
  // Every play sets the element's rate, so a clip that passes none is reset to
  // 1x after a tutor clip at 0.5x.
  const startRate = startRateNow();
  applyElementRate(startRate);
  noteCurrentClipRate(rate, startRate);
  try {
    await audio.play();
    // Stopped while the element was starting - chat teardown, "stop talking
    // now", or this clip's own transport pause. Browsers normally reject the
    // pending play() when pause() lands, but nothing may start after a stop
    // even where one does not.
    if (isStalePlay(request)) {
      if (playbackState.currentAudioId === audioId) audio.pause();
      return false;
    }
    // After the await, not before: a refused play (blocked autoplay) must not
    // report a wait that never ended in audio.
    stampReplyAudioStart(lagContext);
    return true;
  } catch (err) {
    console.warn("Audio play failed:", err);
  }
  return false;
}

// Stops the shared player. With a targetId the iOS plugin pauses only if that
// clip is still the one it holds (see NativeAudioPlayerPlugin.pause); without
// one it pauses whatever it holds, which is what "stop talking now" means.
function stopSharedPlayer(targetId = null) {
  if (useNative) {
    const paused = targetId ? NativeAudio.pause({ id: targetId }) : NativeAudio.pause();
    paused.catch(() => {});
    // Snapshot where the clip stopped, so the resume re-anchors from here.
    if (playbackState.isPlaying) {
      const stoppedAt = nativeClockMediaTime();
      playbackState.currentTime = stoppedAt;
      anchorNativeClock(stoppedAt, nativeClockRate);
    }
    nativeClockGeneration++;
    cancelNativeProgress();
    playbackState.isPlaying = false;
    return;
  }
  if (audio) audio.pause();
}

// silenceUpcomingReplies distinguishes the two intents that both arrive here.
//
// The default - AudioRecorder.startRecording and router.afterEach on every
// in-app navigation - means "stay quiet". Every segment session ends as
// silenced by the learner (and a session created later for a reply that was
// already on screen starts that way: see lastStopRequestAt), and every play()
// still loading is cancelled, so a clip whose load had not finished cannot
// start afterwards on the screen the learner moved to or into their recording.
//
// false - AudioPlayer's transport tap - means only "stop THIS clip". It ends a
// segment session only if that session's own clip is the one sounding, and it
// cancels only a load of that same clip. Pausing an older clip so as to hear
// the reply that is arriving must not silence that reply.
//
// The media element's own `pause` EVENT is a different thing entirely and
// deliberately does not reach this path - it fires immediately before `ended` on
// natural completion, so keying on it would cancel the session at every
// sentence boundary.
export function pause({ silenceUpcomingReplies = true } = {}) {
  if (!silenceUpcomingReplies) {
    pauseCurrentClipOnly();
    return;
  }
  lastStopRequestAt = Date.now();
  // "Stay quiet" covers a phrase slice too: its player, any range still
  // loading, its loop and its backstop timer.
  stopActiveSlice();
  // Recorded on EVERY session, including one that already ended. The end
  // reason is written once and stays what actually ended the session (a
  // session that failed stays failed), but a stop that comes afterwards is a
  // separate fact the final attach must still honor: autoplaying the stitched
  // file of a failed reply into a recording the learner has since started is
  // exactly what they asked not to happen.
  segmentSessions.forEach((session) => {
    session.quietRequested = true;
  });
  endSegmentSessionsExcept(null, "silencedByLearner");
  // Invalidate any play() still waiting on prime(), on `canplay`, or on the
  // native bridge. Those calls check this counter after each await, so bumping
  // it here is what makes "stop talking now" actually stop a clip whose load
  // had not finished yet - otherwise the load completes and starts the audio
  // after the learner has already left the screen or begun recording.
  playGeneration++;
  stopSharedPlayer();
}

function pauseCurrentClipOnly() {
  const clip = playbackState.currentAudioId;
  // Nothing is loaded, so there is no clip for "stop THIS clip" to mean - and
  // on iOS an untargeted pause here could land on a load still crossing the
  // bridge.
  if (!clip) return;
  segmentSessions.forEach((session) => {
    if (session.playingAudioId === clip) {
      endSegmentSession(session, "silencedByLearner");
    }
  });
  inFlightPlays.forEach((request) => {
    if (clip && request.audioId === clip) request.cancelled = true;
  });
  // On iOS a different clip's load may be crossing the bridge right now -
  // typically the reply the learner paused this clip to hear. The plugin runs
  // calls in order on its main queue, so an untargeted pause could land AFTER
  // that load has replaced the player and pause the reply instead. Naming the
  // clip makes the plugin pause it only while it is still the one loaded; if
  // the incoming load already replaced it, it is stopped anyway. The web
  // element has no such race: pausing it now pauses this clip, and a pending
  // load's own play() starts the new source afterwards.
  stopSharedPlayer(clip);
}

export function seek(time) {
  if (!isFinite(time)) return;
  if (useNative) {
    NativeAudio.seek({ time }).catch(() => {});
    // While paused the new position is the snapshot the resume re-anchors
    // from, so a pause before the seek is never counted. While playing, the
    // clock re-anchors now at the rate it is running at.
    playbackState.currentTime = time;
    nativeClockGeneration++;
    if (playbackState.isPlaying) anchorNativeClock(time, nativeClockRate);
    return;
  }
  if (audio) {
    audio.currentTime = time;
    playbackState.currentTime = time;
  }
}

// ---------- Phrase slices ----------
//
// A phrase slice plays one span of a tutor clip, from `start` to `end` in media
// seconds, on a SECOND player, at the speed chip's rate. The pill's own player
// is only paused (through the clip-only variant), so its position, its
// waveform and its pause state stay exactly where the learner left them.
// Swapping the pill's source would lose all three, and on iOS would deallocate
// the main AVAudioPlayer.
//
// The bytes come from the clip cache (src/scripts/clipCache.js), which a pill
// with playback controls fills while phraseReplay is on. On the web and Android
// the slice element plays the cache's object URL, stops at the span's end
// through a requestAnimationFrame loop reading currentTime (timeupdate fires
// only about every 250 ms, and the next span can begin 150 ms after a join),
// and has a setTimeout backstop computed from the remaining media time divided
// by the rate. On iOS the plugin's playRange plays the span on its own second
// AVAudioPlayer and stops it with a native timer.
//
// The guards, each pinned by tests/unit/slicePlayback.test.js or
// tests/unit/slicePlaybackNative.test.js:
//
//   - Every slice carries a token. A stop (pause(), a main play(), a
//     recording starting, backgrounding, a dialog teardown) bumps the token,
//     and every step after an await, every animation frame, every backstop
//     timer, every loop iteration and every rangeEnded event checks it before
//     acting, so nothing stale can stop a newer slice or restart an old one.
//   - A slice takes the next number of the playback-intent generation that
//     play() uses, and checks it again immediately before it starts sounding.
//     A pill play still loading when a phrase is tapped therefore finds its
//     generation stale and never starts, while a reply started after the tap
//     takes a newer number, stops the slice and plays.
//   - While the recording store says a take is in progress no slice starts,
//     and a take beginning stops a sounding one, loop included.
//   - A loop ends on any pause this service did not cause (an OS
//     interruption, a headset button), so it is never restarted over the
//     learner's intent.

// How long the web backstop waits past the moment the span should end. The
// animation frame loop is what normally stops a slice; the backstop exists for
// a throttled frame loop and should not cut the span short when the element
// started a moment late.
export const SLICE_BACKSTOP_SLACK_MS = 100;
// How long the slice element may take, once the clip's bytes are on the
// device, to report its metadata and start playing (and a loop iteration to
// restart). The bytes are local, so this is generous; it exists so an element
// that never answers cannot leave a phrase loading, or a loop sounding
// nothing, until the learner taps something else.
export const SLICE_START_TIMEOUT_MS = 5000;

let sliceTokenSeq = 0;
let activeSlice = null;
// The token of the slice whose source the slice element holds, or null.
let sliceElementOwner = null;
// Bumped whenever the slice element's priming clip is superseded, so a prime
// that resolves late does not pause a real slice.
let slicePrimeSeq = 0;
// Whether the iOS plugin reported both rate and range support.
let nativeSliceSupported = false;
// The ids of the two clips whose bytes the iOS plugin was most recently sent,
// oldest first, mirroring the plugin's own two-slot store.
const nativeDataSlots = [];

function noteNativeClipData(audioId) {
  if (!audioId) return;
  const at = nativeDataSlots.indexOf(audioId);
  if (at >= 0) nativeDataSlots.splice(at, 1);
  nativeDataSlots.push(audioId);
  while (nativeDataSlots.length > 2) nativeDataSlots.shift();
}

function forgetNativeClipData(audioId) {
  const at = nativeDataSlots.indexOf(audioId);
  if (at >= 0) nativeDataSlots.splice(at, 1);
}

// The muted silent clip, played synchronously on every tap, so the element
// the slice will use has been started inside the tap's activation window.
// WebKit otherwise refuses the real play(), which runs after awaiting the clip
// and its metadata, outside that window. A refused prime is never permanent,
// because the next tap primes again.
function primeSliceElement() {
  if (!sliceAudio) return;
  const seq = ++slicePrimeSeq;
  try {
    sliceAudio.muted = true;
    sliceAudio.src = SILENT_MP3;
    Promise.resolve(sliceAudio.play())
      .then(() => {
        if (seq !== slicePrimeSeq) return;
        sliceAudio.pause();
        sliceAudio.muted = false;
      })
      .catch(() => {
        if (seq === slicePrimeSeq) sliceAudio.muted = false;
      });
  } catch (err) {
    console.warn("Slice player prime failed:", err);
  }
}

// Slices always follow the chip, because only a pill with playback controls
// hands out phrase taps.
function currentSliceRate() {
  const rate = effectivePlaybackRate.value;
  return rate > 0 && isFinite(rate) ? rate : 1;
}

function applySliceElementRate(rate) {
  if (!sliceAudio) return;
  try {
    sliceAudio.defaultPlaybackRate = rate;
    sliceAudio.playbackRate = rate;
    sliceAudio.preservesPitch = true;
    sliceAudio.webkitPreservesPitch = true;
  } catch (err) {
    console.warn("Could not set the slice playback rate:", err);
  }
}

function resetSliceState() {
  sliceState.status = "idle";
  sliceState.audioId = null;
  sliceState.start = 0;
  sliceState.end = 0;
  sliceState.loop = false;
  sliceState.owner = null;
}

function sliceIsCurrent(slice) {
  return !!slice && activeSlice === slice && slice.token === sliceTokenSeq;
}

// Whether the slice may start sounding now: still the current slice, no play
// requested since the tap, and no recording in progress.
function sliceMayStart(slice) {
  return (
    sliceIsCurrent(slice) &&
    slice.generation === playGeneration &&
    !recordingState.takeInProgress
  );
}

function clearSliceWatch(slice) {
  if (!slice) return;
  if (slice.rafId != null) {
    cancelAnimationFrame(slice.rafId);
    slice.rafId = null;
  }
  if (slice.backstop) {
    clearTimeout(slice.backstop);
    slice.backstop = null;
  }
  if (slice.startLimit) {
    clearTimeout(slice.startLimit);
    slice.startLimit = null;
  }
}

// Ends the slice if it has not started sounding within SLICE_START_TIMEOUT_MS.
// Stopping aborts the slice's stopper, which wakes the await it is parked on.
function armSliceStartLimit(slice) {
  if (slice.startLimit) clearTimeout(slice.startLimit);
  slice.startLimit = setTimeout(() => {
    slice.startLimit = null;
    if (!sliceIsCurrent(slice)) return;
    console.warn("Phrase slice did not start in time; giving up.");
    stopActiveSlice();
  }, SLICE_START_TIMEOUT_MS);
}

function clearSliceStartLimit(slice) {
  if (!slice.startLimit) return;
  clearTimeout(slice.startLimit);
  slice.startLimit = null;
}

// The element's play(), settled early when the slice is stopped: a play()
// that never settles would otherwise hold the start open. Its own rejection is
// always observed, so an abandoned play() never surfaces as unhandled.
function playSliceElement(slice) {
  const playing = Promise.resolve(sliceAudio.play());
  playing.catch(() => {});
  const stopped = new Promise((resolve) => {
    const signal = slice.stopper.signal;
    if (signal.aborted) resolve();
    else signal.addEventListener("abort", () => resolve(), { once: true });
  });
  return Promise.race([playing, stopped]);
}

// Removes the source without revoking it: the object URL belongs to the clip
// cache, and the next slice or loop iteration plays it again.
function releaseSliceElement() {
  if (!sliceAudio) return;
  sliceElementOwner = null;
  slicePrimeSeq++;
  try {
    sliceAudio.pause();
  } catch {
    // Nothing to pause.
  }
  try {
    sliceAudio.removeAttribute("src");
    sliceAudio.load();
  } catch (err) {
    console.warn("Could not release the slice player's source:", err);
  }
}

// Stops the slice that is sounding or loading, its loop and its timers. The
// state is reset BEFORE the element is paused, so the pause this causes is
// never mistaken for an interruption.
function stopActiveSlice() {
  const slice = activeSlice;
  sliceTokenSeq++;
  activeSlice = null;
  clearSliceWatch(slice);
  // Wakes every await of the stopped slice (its clip fetch, its element's
  // load), so its promise settles instead of waiting on something that may
  // never come, and gives up its share of a shared fetch.
  if (slice && slice.stopper) slice.stopper.abort();
  const wasActive = !!slice || sliceState.status !== "idle";
  resetSliceState();
  if (!wasActive) return;
  if (useNative) {
    // Sent now, not in a later microtask, and aimed at this slice's own range:
    // the tap that replaces this slice sends its playRange in the same tick
    // when the plugin already holds the clip's bytes, and an untargeted stop
    // arriving after it would silence the new phrase. A slice that never sent
    // a range has nothing to stop; the plugin's own pause and play stop the
    // slice player too.
    if (slice && slice.nativeToken) stopNativeRange(slice.nativeToken);
  } else {
    releaseSliceElement();
  }
  evictUnheldClips();
}

function stopNativeRange(token) {
  if (!nativeSliceSupported || typeof NativeAudio.stopRange !== "function") return;
  try {
    Promise.resolve(NativeAudio.stopRange({ token })).catch(() => {});
  } catch {
    // The bridge refused the call; the range ends on its own timer.
  }
}

// A slice that cannot continue ends here, but only if it is still the current
// one: a newer slice or a stop has already taken over otherwise.
function abandonSlice(slice) {
  if (sliceIsCurrent(slice)) stopActiveSlice();
  return false;
}

/**
 * Plays one span of a tutor clip on the slice player, at the speed chip's
 * rate. Must be called synchronously from the tap's handler, so the slice
 * element is primed inside the tap's activation window.
 *
 * @param {{audioId: string, start: number, end: number, loop?: boolean,
 *   owner?: object}} request start and end in media seconds; the end is
 *   clamped to the player's reported duration. owner identifies the phrase for
 *   sliceState and stopSlicesFor, normally {dialogId, messageKey, spanIndex}.
 * @returns {Promise<boolean>} true once the slice is sounding; false when it
 *   was refused, failed, or was stopped or superseded before it started.
 */
export function playSlice({ audioId, start, end, loop = false, owner = null } = {}) {
  const startTime = Number(start);
  const endTime = Number(end);
  if (!audioId || !isFinite(startTime) || !isFinite(endTime)) return Promise.resolve(false);
  if (startTime < 0 || endTime <= startTime) return Promise.resolve(false);
  if (recordingState.takeInProgress) return Promise.resolve(false);
  // An iOS build with the old plugin plays no slices; its pills keep working.
  if (useNative && !nativeSliceSupported) return Promise.resolve(false);
  // The flag is the kill switch for the shared clip fetch, so the service
  // enforces it here as well as the controls do.
  if (!clipCacheState.enabled) return Promise.resolve(false);
  // Wait on the clip BEFORE the previous slice lets go of its own wait. When
  // the previous tap was for the same clip and is still downloading it, this
  // joins that download; stopping the previous slice first would leave the
  // shared fetch with no consumer, abort it, and start it again from zero.
  const stopper = new AbortController();
  const clipRequest = peekClip(audioId)
    ? null
    : getClip(audioId, { priority: "high", signal: stopper.signal });
  if (clipRequest) clipRequest.catch(() => {});
  stopActiveSlice();
  // Before the first await, inside the tap.
  primeSliceElement();
  sliceState.taps++;
  const slice = {
    token: ++sliceTokenSeq,
    generation: ++playGeneration,
    audioId,
    start: startTime,
    end: endTime,
    iteration: 0,
    rafId: null,
    backstop: null,
    nativeToken: null,
    blob: null,
    restartingFromEnded: false,
    stopper,
    clipRequest,
    startLimit: null,
    earlyRangeEnd: null,
    rateRequestSeq: 0,
  };
  activeSlice = slice;
  sliceState.status = "loading";
  sliceState.audioId = audioId;
  sliceState.start = startTime;
  sliceState.end = endTime;
  sliceState.loop = !!loop;
  sliceState.owner = owner;
  // The pill's player pauses, and only its current clip: a load for another
  // clip is left alone here and finds its generation stale instead. On iOS
  // this reaches the plugin before playRange does.
  // The element's own paused flag as well as the reactive state: play() sets
  // paused to false at once, while the play event that sets isPlaying comes
  // later, and a clip starting in that interval would sound over the phrase.
  if (playbackState.isPlaying || (audio && !audio.paused)) pauseCurrentClipOnly();
  // Whether or not a clip was sounding: a load of another clip can be on the
  // bridge while one plays (a reply's autoplay, a pill just pressed).
  if (useNative) stopMainRequestsOnTheBridge();
  // Every other live segment session is superseded, the way any other clip
  // taking the player supersedes it: a reply whose next sentence was still
  // loading would otherwise play it over the phrase, or fail and autoplay
  // its stitched file over it.
  endSegmentSessionsExcept(null, "superseded");
  // A reply that already failed before this tap, and whose stitched file has
  // not attached or finished downloading yet, must not autoplay over the
  // phrase later: the learner chose the phrase after that reply went quiet.
  // A reply that arrives after the tap has no session yet, so it keeps its
  // own fallback.
  segmentSessions.forEach((session) => {
    session.phraseTappedSince = true;
  });
  return runSlice(slice);
}

// A pill resume or load already on its way across the bridge reaches the
// plugin before anything sent now, and the generation bump in playSlice cannot
// call it back. A resume would start the pill's clip under the range, so a
// pause is sent after it and before playRange. A load cannot be cancelled by a
// pause: the plugin decodes in the background and starts the new player when
// the decode lands if its token is still the latest one requested, and only
// stop() clears that token. So a load on the bridge is met with stop(), which
// drops the plugin's player; the service then records that nothing is loaded,
// so the next press of any pill loads it afresh.
//
// Only the most recently sent load can still start: the plugin drops the
// decode of any earlier one when it lands, because a newer token was
// requested since. A superseded load still waiting for its answer therefore
// needs nothing, and stopping for it would only throw away the player of the
// clip the learner last played.
function stopMainRequestsOnTheBridge() {
  const loadOnBridge = !!latestNativeLoad && inFlightPlays.has(latestNativeLoad);
  let resumeOnBridge = false;
  inFlightPlays.forEach((request) => {
    if (request.nativeResumeSent && !request.nativeLoadSent) resumeOnBridge = true;
  });
  if (loadOnBridge) {
    // A clip sounding now is stopped with it: that load was about to replace
    // it anyway, and the phrase takes the player.
    try {
      Promise.resolve(NativeAudio.stop()).catch(() => {});
    } catch {
      // The bridge refused the call; the stale load's own answer pauses it.
    }
    cancelNativeProgress();
    playbackState.isPlaying = false;
    playbackState.currentAudioId = null;
    nativeInstalledToken = null;
    currentClip.followsChip = false;
    anchorNativeClock(0, 1);
  } else if (resumeOnBridge && !playbackState.isPlaying) {
    stopSharedPlayer();
  }
}

/** Turns the loop on or off for the slice that is sounding or loading. */
export function setSliceLoop(loop) {
  if (sliceState.status === "idle") return;
  sliceState.loop = !!loop;
}

/** Stops any slice, and its loop. */
export function stopSlice() {
  stopActiveSlice();
}

async function runSlice(slice) {
  let blob = slice.clipRequest ? null : peekClip(slice.audioId);
  if (!blob) {
    // The same time limit as the stitched download: a stalled request must
    // not leave the phrase loading until the learner taps something else.
    const limit = setTimeout(() => slice.stopper.abort(), STITCHED_FETCH_TIMEOUT_MS);
    try {
      blob = await (slice.clipRequest ||
        getClip(slice.audioId, { priority: "high", signal: slice.stopper.signal }));
    } catch (err) {
      if (err && err.name !== "AbortError") console.warn("Phrase slice load failed:", err);
      return abandonSlice(slice);
    } finally {
      clearTimeout(limit);
    }
  }
  if (!sliceMayStart(slice)) return abandonSlice(slice);
  slice.blob = blob;
  return useNative ? startNativeSlice(slice) : startWebSlice(slice);
}

// Resolves once the slice element has metadata or its load failed, or once the
// slice was stopped. Deliberately not on `emptied` or `abort`: replacing a
// source that was already set (the prime's silent clip) fires both before the
// new clip has loaded anything.
function waitForSliceElement(slice) {
  if (sliceAudio.readyState >= 1) return Promise.resolve();
  return new Promise((resolve) => {
    const events = ["loadedmetadata", "canplay", "error"];
    const signal = slice.stopper.signal;
    const settle = () => {
      events.forEach((name) => sliceAudio.removeEventListener(name, settle));
      signal.removeEventListener("abort", settle);
      resolve();
    };
    events.forEach((name) => sliceAudio.addEventListener(name, settle));
    signal.addEventListener("abort", settle);
    if (signal.aborted) settle();
  });
}

async function startWebSlice(slice) {
  const url = clipObjectUrl(slice.audioId);
  if (!url) return abandonSlice(slice);
  // The prime has done its job; the real source replaces it.
  slicePrimeSeq++;
  sliceAudio.muted = false;
  sliceElementOwner = slice.token;
  armSliceStartLimit(slice);
  sliceAudio.src = url;
  await waitForSliceElement(slice);
  if (!sliceMayStart(slice)) return abandonSlice(slice);
  // The clip failed to load; playing now would only report a start that
  // never makes a sound.
  if (sliceAudio.error) {
    console.warn("Phrase slice clip failed to load:", sliceAudio.error);
    return abandonSlice(slice);
  }
  // The last span's end comes from decoded audio, while the player reports
  // its own length; the player's is the one it can actually reach.
  const duration = sliceAudio.duration;
  if (isFinite(duration) && duration > 0 && slice.end > duration) {
    slice.end = duration;
    sliceState.end = duration;
  }
  if (slice.end <= slice.start) return abandonSlice(slice);
  sliceAudio.currentTime = slice.start;
  applySliceElementRate(currentSliceRate());
  try {
    await playSliceElement(slice);
  } catch (err) {
    console.warn("Phrase slice play failed:", err);
    return abandonSlice(slice);
  }
  if (!sliceMayStart(slice)) {
    // A stop landed while the element was starting. Browsers normally reject
    // the pending play() then, but nothing may keep sounding where one does
    // not.
    if (sliceElementOwner === slice.token) releaseSliceElement();
    return abandonSlice(slice);
  }
  clearSliceStartLimit(slice);
  sliceState.status = "playing";
  armSliceWatch(slice);
  return true;
}

function armSliceWatch(slice) {
  clearSliceWatch(slice);
  const iteration = slice.iteration;
  const positionAtArm = sliceAudio.currentTime || 0;
  const remaining = Math.max(0, slice.end - positionAtArm);
  slice.backstop = setTimeout(() => {
    slice.backstop = null;
    if (!sliceIsCurrent(slice) || slice.iteration !== iteration) return;
    // An element whose output started late is still short of the span's
    // end but moving: give it the time its remaining media needs rather
    // than cutting the last syllable. One that has not moved since the
    // backstop was armed is stuck, and the slice ends, loop included, so a
    // loop never keeps re-arming over silence.
    const position = sliceAudio.currentTime || 0;
    if (position >= slice.end) {
      finishSliceIteration(slice);
    } else if (!sliceAudio.paused && position > positionAtArm) {
      armSliceWatch(slice);
    } else {
      stopActiveSlice();
    }
  }, (remaining * 1000) / currentSliceRate() + SLICE_BACKSTOP_SLACK_MS);
  const frame = () => {
    slice.rafId = null;
    if (!sliceIsCurrent(slice) || sliceState.status !== "playing") return;
    if (slice.iteration !== iteration) return;
    if ((sliceAudio.currentTime || 0) >= slice.end) {
      finishSliceIteration(slice);
      return;
    }
    slice.rafId = requestAnimationFrame(frame);
  };
  slice.rafId = requestAnimationFrame(frame);
}

// The span reached its end. A loop starts it again; otherwise the slice ends.
function finishSliceIteration(slice) {
  if (!sliceIsCurrent(slice) || sliceState.status !== "playing") return;
  clearSliceWatch(slice);
  if (!sliceState.loop || recordingState.takeInProgress) {
    stopActiveSlice();
    return;
  }
  slice.iteration++;
  if (useNative) {
    startNativeSlice(slice);
  } else {
    restartWebSlice(slice);
  }
}

async function restartWebSlice(slice) {
  sliceAudio.currentTime = slice.start;
  if (!sliceAudio.paused) {
    armSliceWatch(slice);
    return;
  }
  // The span ended at the clip's own end, so the element stopped by itself.
  // The pause and ended events that stop produced must not be read as an
  // interruption while it starts again.
  slice.restartingFromEnded = true;
  armSliceStartLimit(slice);
  try {
    await playSliceElement(slice);
  } catch (err) {
    console.warn("Phrase slice loop restart failed:", err);
    slice.restartingFromEnded = false;
    abandonSlice(slice);
    return;
  }
  slice.restartingFromEnded = false;
  if (!sliceMayStart(slice)) {
    if (sliceElementOwner === slice.token) releaseSliceElement();
    abandonSlice(slice);
    return;
  }
  clearSliceStartLimit(slice);
  armSliceWatch(slice);
}

if (sliceAudio) {
  sliceAudio.addEventListener("pause", () => {
    const slice = activeSlice;
    if (!sliceIsCurrent(slice) || sliceState.status !== "playing") return;
    if (slice.restartingFromEnded) return;
    // Only the element reaching the end of its media is a natural end here:
    // the animation frame loop and the backstop stop a span that ends before
    // the clip does, and they never pause. A pause close to the span's end is
    // still an interruption.
    if (sliceAudio.ended) {
      finishSliceIteration(slice);
      return;
    }
    // A pause this service did not cause. The loop ends here rather than
    // restarting over whatever interrupted it.
    stopActiveSlice();
  });
  // A media error after the slice started (a decode failure part way through)
  // stops the element without a pause or ended event, so without this a loop
  // would keep re-arming its timers over silence. The element's error is
  // checked because an error event queued by the priming clip can arrive
  // after the real source was set, when the new load has reset it.
  sliceAudio.addEventListener("error", () => {
    const slice = activeSlice;
    if (!sliceIsCurrent(slice) || sliceState.status !== "playing") return;
    if (!sliceAudio.error) return;
    console.warn("Phrase slice playback failed:", sliceAudio.error);
    stopActiveSlice();
  });
  sliceAudio.addEventListener("ended", () => {
    const slice = activeSlice;
    if (!sliceIsCurrent(slice) || sliceState.status !== "playing") return;
    if (slice.restartingFromEnded) return;
    finishSliceIteration(slice);
  });
}

// iOS: the plugin's playRange plays the span on its second player. The data
// is sent only when the clip is not one of the two the plugin was most
// recently sent; when the plugin answers {needsData: true} anyway, the call is
// sent once more with the data, so a mismatch costs one bridge round trip and
// never a dead tap. Each call carries its own token, which rangeEnded echoes.
async function startNativeSlice(slice) {
  const token = `range-${slice.token}-${slice.iteration}`;
  slice.nativeToken = token;
  const options = {
    id: slice.audioId,
    start: slice.start,
    end: slice.end,
    rate: currentSliceRate(),
    token,
  };
  // A fresh options object per call, so the first call's arguments are never
  // changed after they were sent.
  const sendRange = async (withData) => {
    const call = { ...options };
    if (withData) {
      call.data = await blobToBase64(slice.blob);
      if (!sliceMayStart(slice)) return null;
    }
    const result = await NativeAudio.playRange(call);
    if (withData) noteNativeClipData(slice.audioId);
    return result || {};
  };
  // A later iteration of this same loop has been sent while this call was on
  // its way (its timer can fire before playRange answers). The plugin cancels
  // this range when the later one arrives; this call must then do nothing
  // that would end the slice the later iteration now carries.
  const replacedByLaterIteration = () => sliceIsCurrent(slice) && slice.nativeToken !== token;
  let result;
  try {
    const hadData = nativeDataSlots.includes(slice.audioId);
    result = await sendRange(!hadData);
    if (replacedByLaterIteration()) {
      stopNativeRange(token);
      return true;
    }
    if (result && result.needsData === true) {
      forgetNativeClipData(slice.audioId);
      // The plugin had just been sent the bytes and still has none: resending
      // would only repeat the refusal.
      if (!hadData) return abandonSlice(slice);
      if (!sliceMayStart(slice)) return abandonSlice(slice);
      result = await sendRange(true);
      if (replacedByLaterIteration()) {
        stopNativeRange(token);
        return true;
      }
      if (result && result.needsData === true) {
        forgetNativeClipData(slice.audioId);
        return abandonSlice(slice);
      }
    }
  } catch (err) {
    console.warn("Native phrase slice failed:", err);
    if (replacedByLaterIteration()) return true;
    // A rejected playRange ends the slice and its loop.
    return abandonSlice(slice);
  }
  if (!result || !sliceMayStart(slice)) {
    // It may have started while the answer crossed the bridge. Stop exactly
    // this range: an untargeted stop could land on a newer slice.
    stopNativeRange(token);
    return abandonSlice(slice);
  }
  sliceState.status = "playing";
  // The range already ended while its answer was on its way; apply that now.
  const earlyEnd = slice.earlyRangeEnd;
  slice.earlyRangeEnd = null;
  if (earlyEnd && earlyEnd.token === token) {
    handleNativeRangeEnded(earlyEnd);
    return sliceIsCurrent(slice);
  }
  // The chip moved while the range crossed the bridge at the rate it was
  // sent with; bring the playing range to the rate the chip shows now.
  if (currentSliceRate() !== options.rate) applySliceRateChange(currentSliceRate());
  return true;
}

function handleNativeRangeEnded(event) {
  const slice = activeSlice;
  if (!event || !sliceIsCurrent(slice) || event.token !== slice.nativeToken) return;
  if (sliceState.status !== "playing") {
    // The plugin can report the end (an interruption, a failed play) before
    // its answer to playRange has reached us. Keep it for startNativeSlice,
    // which applies it once the range counts as playing.
    slice.earlyRangeEnd = event;
    return;
  }
  if (event.reason === "timer") {
    finishSliceIteration(slice);
    return;
  }
  // Interrupted, failed or preempted: the slice is over, loop included.
  stopActiveSlice();
}

// The chip moved while a slice sounds. The web element takes the new rate and
// its backstop is recomputed from the remaining media time; on iOS the plugin
// re-arms its slice timer the same way when it applies the rate.
function applySliceRateChange(nextRate) {
  const slice = activeSlice;
  if (!sliceIsCurrent(slice)) return;
  if (useNative) {
    if (sliceState.status !== "playing" || !slice.nativeToken) return;
    if (typeof NativeAudio.setRate !== "function") return;
    const token = slice.nativeToken;
    // Counts this slice's rate requests: cycling the chip can ask for the
    // same rate twice, so the rate alone cannot tell an obsolete refusal from
    // the latest one.
    const requestSeq = ++slice.rateRequestSeq;
    Promise.resolve()
      .then(() =>
        NativeAudio.setRate({
          rate: nextRate,
          id: slice.audioId,
          token,
          range: true,
        })
      )
      .catch((err) => {
        console.warn("The native slice player refused the new rate:", err);
        // The phrase keeps sounding at a speed the chip no longer shows, so it
        // stops, unless a newer slice, a newer range of this loop or another
        // rate change has already taken over.
        if (!sliceIsCurrent(slice) || slice.nativeToken !== token) return;
        if (requestSeq !== slice.rateRequestSeq) return;
        stopActiveSlice();
      });
    return;
  }
  applySliceElementRate(nextRate);
  if (sliceState.status === "playing") armSliceWatch(slice);
}

// Backgrounding does not pause an HTML audio element by itself, and a hidden
// page stops animation frames and throttles timers, so the slice player is
// paused directly. Native WebViews do not reliably fire visibilitychange
// (ChatScreen and AudioRecorder note the same), so on native the app state is
// watched too.
if (typeof document !== "undefined" && typeof document.addEventListener === "function") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") stopActiveSlice();
  });
}
if (typeof Capacitor.isNativePlatform === "function" && Capacitor.isNativePlatform()) {
  import("@capacitor/app")
    .then(({ App }) =>
      App.addListener("appStateChange", ({ isActive }) => {
        if (!isActive) stopActiveSlice();
      })
    )
    .catch((err) => console.warn("Could not watch the app state for slices:", err));
}

// A take starting silences a slice even if its host never called pause().
watch(
  () => recordingState.takeInProgress,
  (inProgress) => {
    if (inProgress) stopActiveSlice();
  },
  { flush: "sync" }
);

// Neither player's clip may be evicted from the cache while it holds it.
registerPlayerHolds(
  (audioId) =>
    !!audioId &&
    (audioId === playbackState.currentAudioId || (activeSlice && activeSlice.audioId === audioId))
);

registerSliceController({
  stopSlicesFor(filter) {
    const owner = sliceState.owner;
    if (sliceState.status === "idle") return;
    if (filter) {
      const matches = Object.keys(filter).every(
        (key) => filter[key] === undefined || (owner && owner[key] === filter[key])
      );
      if (!matches) return;
    }
    stopActiveSlice();
  },
  // The phrase controls in TutorMessage and AudioPlayer call these through
  // slicePlayback.js rather than importing the service.
  playSlice: (request) => playSlice(request),
  setSliceLoop: (loop) => setSliceLoop(loop),
  cancelPendingPlaysOf: (audioId) => cancelPlaysOf(audioId),
  supersedeOtherPendingPlays: (audioId) => {
    inFlightPlays.forEach((request) => {
      if (request.audioId !== audioId && !request.nativeLoadSent) request.cancelled = true;
    });
  },
  anySegmentSessionLive: () =>
    Object.values(segmentPlaybackState.phases).some(
      (phase) => phase === "playing" || phase === "waiting"
    ),
});

// ---------- Segmented reply playback ----------
//
// When the backend splits a tutor reply into a fast first sentence plus the
// remainder, it writes each piece's audio id under the message's `segments`
// child as it becomes available, and only later attaches the stitched whole
// file as the message's `audioId`. A segment session is what plays that run of
// clips as one continuous reply.
//
// It is a session rather than a queue because the rules are about STATE, not
// about ordering:
//
//   - it advances only on a natural-end event, and only when the id that ended
//     is the one this session asked for. It keeps that record itself: the
//     `ended` handler above clears `currentAudioId` and revokes the object URL,
//     so module state is already gone by the time anything else could read it.
//   - it ends on the exported pause() and on any other clip taking the shared
//     player, and an ended session NEVER resumes on its own. Without that rule
//     a learner who navigated away during the gap between segments would hear
//     the tutor start speaking on another screen with no visible player.
//     Recovery is the stitched player the final attach mounts.
//   - it has a real waiting state, entered when a segment ends before the next
//     one has arrived. A segment arriving while the session is actively waiting
//     starts playing; the same segment arriving after the session ended does
//     not.
//
// How a session ENDED is recorded once, as one reason from a closed set, by the
// one code site that actually knows which of them happened:
//
//   - "completed"          the last segment ended naturally and the stitched
//                          file says no further segment is coming;
//   - "failed"             the session could not continue: a segment's bytes
//                          never arrived, a start was refused, the element
//                          reported an error, or a watchdog ran out;
//   - "silencedByLearner"  the learner asked for quiet: a record tap, a
//                          navigation, or pausing this session's own clip;
//   - "superseded"         something else took the player: a newer reply's
//                          session, or any other clip.
//
// The reason is never inferred afterwards from other state. Whether the
// learner HEARD any of the reply is a separate fact, set only when audible
// playback progress is actually observed (noteAudibleProgress). The final
// attach combines the two (completeSegmentSession).
//
// There is deliberately no exported stop(): this service has never had one, and
// all three callers the design names ("stop talking now") already route through
// pause().

const segmentSessions = new Map(); // sessionKey -> session record
let activeSegmentSessionKey = null;

// When something last meant "stop talking now". A session cannot be ended
// before it exists, and there is a real window in which none exists yet: the
// mic comes back when the reply TEXT arrives, so a learner can tap record
// before the first segment has been written. Comparing this against the moment
// the reply's text rendered is what distinguishes the two cases that look
// identical from inside the service - a stop that came AFTER this reply was on
// screen (the learner does not want to hear it) from one that came BEFORE it
// existed, such as the navigation that brought them into the chat.
let lastStopRequestAt = 0;

// Phase per session, exposed reactively so a message can render its speaking
// indicator and gate its progressive text reveal without reaching into the
// session records. Phases are "playing", "waiting", "suspended" and
// "completed". The last two are both terminal: "completed" is the end reason
// of the same name, and "suspended" is every other end reason.
export const segmentPlaybackState = reactive({
  phases: {},
});

function segmentSessionKey(dialogId, messageKey) {
  return `${dialogId}/${messageKey}`;
}

function setSegmentPhase(session, phase) {
  session.phase = phase;
  segmentPlaybackState.phases[session.key] = phase;
}

function activeSegmentSession() {
  if (!activeSegmentSessionKey) return null;
  return segmentSessions.get(activeSegmentSessionKey) || null;
}

// Fetches a segment's bytes at most once per session, and as soon as the
// segment is announced rather than at the boundary, so the handoff between two
// sentences is a source swap instead of a network round trip.
function loadSegmentBlob(session, index) {
  const audioId = session.segments[index];
  if (!audioId) return Promise.reject(new Error("No audio id for segment " + index));
  let pending = session.blobs.get(index);
  if (pending) return pending;
  if (typeof session.fetchSegment !== "function") {
    return Promise.reject(new Error("No segment fetcher was supplied"));
  }
  pending = Promise.resolve().then(() => session.fetchSegment(audioId));
  session.blobs.set(index, pending);
  // Settles when this fetch does, without holding its bytes, so the stitched
  // clip's prefetch can wait for every segment fetch the session started.
  session.segmentFetches.push(pending.then(() => {}, () => {}));
  // A failed fetch is not cached. The remainder is prefetched the moment it is
  // announced, and one transient error then must not doom the boundary
  // seconds later: whoever loads it next fetches it again. Whoever was already
  // awaiting this promise still sees the rejection.
  pending.catch(() => {
    if (session.blobs.get(index) === pending) session.blobs.delete(index);
  });
  return pending;
}

// A session that is still the one allowed to reach the player. Every one of
// these can change while a segment's bytes are in flight, and the session
// object itself outlives its map entry (an in-flight fetch holds a reference),
// so all of them are checked again after each await rather than only before it.
function ownsThePlayer(session, audioId) {
  return (
    !session.retired &&
    !session.endReason &&
    session.phase === "playing" &&
    session.playingAudioId === audioId &&
    activeSegmentSessionKey === session.key
  );
}

// Taking the player away from whatever session held it. Two replies can be in
// flight at once (the mic is handed back when the TEXT arrives, before the
// audio does), and without this the older session stays "playing" and can talk
// over the newer one when its own fetch lands late. The older session did not
// fail and nobody silenced it; a newer reply displaced it.
function claimActiveSegmentSession(session) {
  const previous = activeSegmentSession();
  if (previous && previous.key !== session.key) {
    endSegmentSession(previous, "superseded");
  }
  activeSegmentSessionKey = session.key;
}

async function advanceSegmentSession(session) {
  // Only an actively waiting session that has not ended or been dropped may
  // start a clip. This is the single gate that keeps an ended session silent,
  // whatever wakes it.
  if (session.retired || session.endReason || session.phase !== "waiting") return;
  const index = session.nextIndex;
  const audioId = session.segments[index];
  if (!audioId) {
    // Nothing left to play. If the stitched file has already been attached,
    // the server is telling us the segment list is final, so the session ends
    // here; otherwise the remainder is still being synthesized and the session
    // keeps waiting, with the learner still shown that Arti is speaking.
    if (session.finalAttachSeen) {
      endExhaustedSegmentSession(session);
      return;
    }
    armSegmentWaitWatchdog(session);
    return;
  }

  setSegmentPhase(session, "playing");
  session.playingIndex = index;
  session.playingAudioId = audioId;
  claimActiveSegmentSession(session);

  let blob;
  try {
    blob = await loadSegmentBlob(session, index);
  } catch (error) {
    // Give up on the session rather than skipping ahead to a later segment: a
    // reply with a sentence missing from the middle is worse than one the
    // learner replays whole from the stitched player the final attach mounts.
    // Only a session still holding the player failed because of this; one
    // that already ended keeps the reason it ended for.
    console.warn("Segment audio load failed:", error);
    if (ownsThePlayer(session, audioId)) endSegmentSession(session, "failed");
    return;
  }

  // The learner may have navigated away, hit record, or played another clip
  // while those bytes were in flight, and the chat may have been torn down.
  if (!ownsThePlayer(session, audioId)) return;

  // Bound the start too. A play() that never settles - neither `canplay` nor
  // `error` ever fires, or the native bridge never answers - would otherwise
  // leave the session "playing" with no audio for the life of the chat. The
  // clip's duration is not known yet, so this uses the ceiling; it is re-armed
  // with the real budget once the clip starts.
  armSegmentEndWatchdog(session, audioId, { beforeStart: true });
  // No lag context here, deliberately: play() stamps when its own start
  // resolves, and a resolved start is not the learner hearing anything.
  // Segment 0's stamp fires on audible progress instead (noteAudibleProgress).
  const started = await play(audioId, blob, null, null, sessionPlaybackRate(session));
  if (!ownsThePlayer(session, audioId)) return;
  // A clip that never started fires no natural-end event, so leaving the
  // session in "playing" would strand it: the message would show "Speaking..."
  // forever with no audio. Ending it hands the learner the stitched player.
  if (!started) {
    endSegmentSession(session, "failed");
    return;
  }
  armSegmentEndWatchdog(session, audioId);
  watchNativeAudibleProgress(session, audioId);
}

// The stitched file has attached, so the segment list is final, and there is
// nothing at the session's next index. Which reason that is depends on what
// the session already did, and only the natural-end handler ever advances
// nextIndex: past 0 means at least one segment played to its natural end.
//
//   - some segment has ended naturally and none is known beyond the gap: the
//     reply ran to its end, "completed";
//   - the FIRST segment never arrived, or a later one is known while the next
//     one is missing: the session can never play what it is waiting for,
//     "failed".
function endExhaustedSegmentSession(session) {
  const knownBeyondGap = session.segments.some(
    (audioId, index) => index > session.nextIndex && audioId
  );
  const reason = session.nextIndex > 0 && !knownBeyondGap ? "completed" : "failed";
  endSegmentSession(session, reason);
}

// The only way a session ends. The reason is set once: whatever happens to the
// session afterwards (a fetch rejecting after the learner silenced it, a second
// suspension) cannot overwrite what actually ended it.
function endSegmentSession(session, reason) {
  if (!session || session.retired || session.endReason) return;
  session.endReason = reason;
  clearSegmentEndWatchdog(session);
  clearSegmentProgressPoll(session);
  // The session may already have handed its clip to play(), which may still
  // be waiting on prime(), on `canplay`, or on the native bridge. Ending the
  // session does not reach that call, so without this the clip starts anyway
  // once its load finishes -- on top of a recording, or on top of the clip
  // that displaced this session. Cancelling only this session's own clip,
  // rather than every load in flight, is what lets a newer reply's load
  // survive the older reply ending.
  cancelPlaysOf(session.playingAudioId);
  setSegmentPhase(session, reason === "completed" ? "completed" : "suspended");
  session.playingIndex = null;
  session.playingAudioId = null;
  releaseSegmentBlobs(session);
  if (activeSegmentSessionKey === session.key) activeSegmentSessionKey = null;
  if (reason === "failed") playStitchedFallback(session);
  prefetchStitchedClip(session);
}

// How long the stitched file's download through the clip cache may take, for
// the fallback and the prefetch alike: the same limit ChatScreen gives a
// segment fetch (SEGMENT_FETCH_TIMEOUT_MS), so a stalled request cannot leave
// a shared cache entry pending for the learner's next press to join.
export const STITCHED_FETCH_TIMEOUT_MS = 10000;

// A signal that aborts after STITCHED_FETCH_TIMEOUT_MS, or when the chat is
// torn down (endAllSegmentSessions aborts every session's stoppers). Kept on
// the session under `key`.
function stitchedFetchSignal(session, key) {
  const controller = new AbortController();
  session[key] = controller;
  const timer = setTimeout(() => controller.abort(), STITCHED_FETCH_TIMEOUT_MS);
  controller.signal.addEventListener("abort", () => clearTimeout(timer), { once: true });
  return { signal: controller.signal, done: () => clearTimeout(timer) };
}

// The stitched file of a reply heard as segments mounts paused with its fetch
// deferred, which is right for the pill but leaves a phrase tap on the reply
// the learner just heard waiting on a download. So once the session has ended
// and the stitched id is known (the two can happen in either order), while
// phraseReplay is on and for a reply whose pill has playback controls, the
// stitched file is fetched into the clip cache: after every segment fetch the
// session started has settled, so it never competes with the segments for
// bandwidth, and at low priority. The pill's own play then finds the same
// bytes in the cache.
function prefetchStitchedClip(session) {
  if (!session || session.retired || !session.endReason) return;
  if (!session.fallbackAudioId || session.stitchedPrefetchStarted) return;
  if (!session.followsSpeedChip || !clipCacheState.enabled) return;
  session.stitchedPrefetchStarted = true;
  const stitchedAudioId = session.fallbackAudioId;
  Promise.all(session.segmentFetches).then(() => {
    // The chat was torn down, or the flag turned off, while they settled.
    if (session.retired || !clipCacheState.enabled) return;
    // Aborted if the chat is torn down while it downloads, or when it outlasts
    // the segment fetch time limit, so the cache gives the fetch up once
    // nobody else is waiting on it and a later press starts a fresh one.
    const limit = stitchedFetchSignal(session, "prefetchStopper");
    getClip(stitchedAudioId, { priority: "low", signal: limit.signal })
      .finally(limit.done)
      .catch(
        (error) => {
          if (error && error.name !== "AbortError") {
            console.warn("Stitched clip prefetch failed:", error);
          }
        }
      );
  });
}

// Plays a session's stitched file when the session failed before the learner
// heard anything. It runs when the failure and the final attach have both
// happened, in whichever order: at the attach for a session that had already
// failed, or at the failure for a session that was still live when the attach
// mounted the player paused. The rule - failed, nothing heard, the learner has
// not asked for quiet, no other clip holds the player - is checked before the
// fetch and again after the bytes arrive, because any of it can change during
// the fetch. If it holds, the stitched file plays exactly as a whole-file reply
// autoplays.
function stitchedFallbackAllowed(session) {
  return (
    !session.retired &&
    session.endReason === "failed" &&
    !session.heard &&
    !session.quietRequested &&
    !session.phraseTappedSince &&
    !anotherClipHoldsThePlayer(session)
  );
}

async function playStitchedFallback(session) {
  const stitchedAudioId = session.fallbackAudioId;
  if (!stitchedAudioId || session.fallbackStarted) return;
  if (typeof session.fetchSegment !== "function") return;
  if (!stitchedFallbackAllowed(session)) return;
  session.fallbackStarted = true;
  const throughCache = session.followsSpeedChip && clipCacheState.enabled;
  let blob;
  try {
    // Through the clip cache while phraseReplay is on, so the prefetch for
    // phrase taps and this fallback share one download. It gets the same
    // time limit ChatScreen gives a segment fetch, and teardown gives it up.
    if (throughCache) {
      const limit = stitchedFetchSignal(session, "fallbackStopper");
      try {
        blob = await getClip(stitchedAudioId, { priority: "high", signal: limit.signal });
      } finally {
        limit.done();
      }
    } else {
      blob = await session.fetchSegment(stitchedAudioId);
    }
  } catch (error) {
    // The paused player is still there; the learner can press play.
    if (!error || error.name !== "AbortError") {
      console.warn("Stitched fallback load failed:", error);
    }
    return;
  }
  // stitchedFallbackAllowed also refuses when a phrase was tapped while this
  // session existed (phraseTappedSince), which covers a tap made while these
  // bytes downloaded, whatever the flag has done since. A reply that arrived
  // after the tap is not marked, and its play() below ends the slice.
  if (!stitchedFallbackAllowed(session)) return;
  // The whole-file path, with this reply's lag context: play() stamps the
  // audio start once playback begins, the same way an autoplayed whole-file
  // reply does.
  await play(stitchedAudioId, blob, null, session.lagContext, sessionPlaybackRate(session));
}

// Every live session, not just the one holding the player, except one whose
// own current segment is `audioId` (the session's own next play). A session
// waiting for its very first segment (the remainder can be written before the
// fast segment) has claimed nothing, so a rule scoped to the active session
// would miss it and let it start speaking over a recording the learner began
// in the meantime.
function endSegmentSessionsExcept(audioId, reason) {
  segmentSessions.forEach((session) => {
    if (audioId && session.playingAudioId === audioId) return;
    endSegmentSession(session, reason);
  });
}

// A suspended or finished session will never fetch or play again - the
// stitched player is the recovery - so the downloaded bytes are dead weight.
// Sessions live until the chat is torn down, so without this a long
// conversation accumulates every segment blob it ever played.
function releaseSegmentBlobs(session) {
  session.blobs.clear();
}

// Audible progress on `audioId` was actually observed: the web element's
// timeline moved past zero, or the iOS plugin reported its player advancing.
// This is the ONLY place a session becomes "heard", and the only place
// segment 0's reply-lag stamp fires, so both mean the learner could hear the
// reply - not that play() resolved, which happens before a frame is decoded.
function noteAudibleProgress(audioId) {
  if (!audioId) return;
  const session = activeSegmentSession();
  if (!session || !ownsThePlayer(session, audioId)) return;
  session.heard = true;
  if (session.playingIndex === 0 && !session.audioStartStamped) {
    session.audioStartStamped = true;
    stampReplyAudioStart(session.lagContext);
  }
}

// The iOS plugin emits no progress event at all - audioEnded is its only
// event - and its play() resolves as soon as AVAudioPlayer.play() returns,
// which is a start request, not sound. Its getStatus() does report the
// player's own id, isPlaying and currentTime, so polling that until the
// timeline moves is the plugin's equivalent of the web element's timeupdate.
// The poll stops the moment the session hears something or stops owning the
// player, so after the first audible frame it costs nothing further.
const NATIVE_PROGRESS_POLL_MS = 100;

function watchNativeAudibleProgress(session, audioId) {
  if (!useNative || session.heard || typeof NativeAudio.getStatus !== "function") {
    return;
  }
  const poll = async () => {
    session.progressPoll = null;
    if (session.heard || !ownsThePlayer(session, audioId)) return;
    let status = null;
    try {
      status = await NativeAudio.getStatus();
    } catch (err) {
      status = null;
    }
    if (session.heard || !ownsThePlayer(session, audioId)) return;
    if (
      status &&
      status.currentId === audioId &&
      status.isPlaying === true &&
      Number(status.currentTime) > 0
    ) {
      noteAudibleProgress(audioId);
      return;
    }
    session.progressPoll = setTimeout(poll, NATIVE_PROGRESS_POLL_MS);
  };
  poll();
}

function clearSegmentProgressPoll(session) {
  if (!session || !session.progressPoll) return;
  clearTimeout(session.progressPoll);
  session.progressPoll = null;
}

// Playback that STARTED and then stopped without completing reaches no
// natural-end event on either platform: iOS emits audioEnded only from
// audioPlayerDidFinishPlaying, so an AVAudioSession interruption (a phone call,
// Siri) is silent, and the web element reports a mid-playback decode failure
// through `error`. Without a bound the session would sit in "playing" for the
// life of the chat, showing the learner that Arti is speaking when nothing is.
// The budget is the clip's own length plus slack, falling back to a ceiling
// when the container's duration is unreadable.
const SEGMENT_END_SLACK_MS = 5000;
const SEGMENT_END_FALLBACK_MS = 120000;

// How long a session may wait for a segment that has not been written yet. The
// remainder normally follows within seconds, but a backend that writes the fast
// segment and then fails before writing either the remainder or the stitched
// file leaves nothing else to end the wait: the reply watchdog in ChatScreen was
// already cleared when the tutor's text arrived, so the message would show that
// Arti is speaking for as long as the learner sits there.
const SEGMENT_WAIT_TIMEOUT_MS = 60000;

function clearSegmentEndWatchdog(session) {
  if (!session || !session.endWatchdog) return;
  clearTimeout(session.endWatchdog);
  session.endWatchdog = null;
  session.endWatchdogAfterStart = false;
}

// The rate a session's clips play at: the effective rate for a session the
// chat marked as following the speed chip, and null (1x) otherwise.
function sessionPlaybackRate(session) {
  return session && session.followsSpeedChip ? effectivePlaybackRate.value : null;
}

function armSegmentEndWatchdog(session, audioId, { beforeStart = false } = {}) {
  clearSegmentEndWatchdog(session);
  // Before the start resolves, playbackState.duration still describes the
  // previous clip, so only the ceiling is meaningful.
  const duration = beforeStart ? 0 : playbackState.duration;
  let budget = SEGMENT_END_FALLBACK_MS;
  if (!beforeStart) {
    // Wall time, not media time: at 0.5x the remaining audio takes twice as
    // long to play, and a budget computed at 1x would end a segment the
    // learner is still hearing. The rate is the one the clip is actually
    // playing at, which is what the service applied, not what the chip says.
    // A clip whose length is unreadable gets the ceiling as its length, so its
    // remainder stretches with the rate the same way. Before the start the
    // ceiling only covers the load, which the rate does not slow.
    const rate = session.followsSpeedChip && currentClip.followsChip ? currentClip.rate : 1;
    const heard = playbackState.currentTime || 0;
    const remaining =
      duration > 0
        ? Math.max(0, duration - heard)
        : Math.max(0, SEGMENT_END_FALLBACK_MS / 1000 - heard);
    budget = (remaining * 1000) / rate + (duration > 0 ? SEGMENT_END_SLACK_MS : 0);
  }
  // A budget armed after the start depends on the rate, so a rate change
  // recomputes it.
  session.endWatchdogAfterStart = !beforeStart;
  session.endWatchdog = setTimeout(() => {
    session.endWatchdog = null;
    if (!ownsThePlayer(session, audioId)) return;
    endSegmentSession(session, "failed");
  }, budget);
}

// The chip moved while a segment was sounding: the budget armed at the old rate
// is recomputed from the remaining media time at the new one.
function rearmSegmentWatchdogForRate() {
  const session = activeSegmentSession();
  if (!session || !session.followsSpeedChip || !session.endWatchdogAfterStart) return;
  if (session.phase !== "playing" || !session.playingAudioId) return;
  if (!ownsThePlayer(session, session.playingAudioId)) return;
  armSegmentEndWatchdog(session, session.playingAudioId);
}

// The same bound for the other state that has no natural end of its own:
// waiting for a segment nobody may ever write.
function armSegmentWaitWatchdog(session) {
  clearSegmentEndWatchdog(session);
  session.endWatchdog = setTimeout(() => {
    session.endWatchdog = null;
    if (session.retired || session.endReason || session.phase !== "waiting") return;
    endSegmentSession(session, "failed");
  }, SEGMENT_WAIT_TIMEOUT_MS);
}

// The clip the session was playing failed outright. Same recovery as the
// watchdog, just immediate.
function handleSegmentPlaybackStalled(failedAudioId) {
  const session = activeSegmentSession();
  if (!session || !failedAudioId || !ownsThePlayer(session, failedAudioId)) return;
  endSegmentSession(session, "failed");
}

// Called from both natural-end paths (the HTML5 `ended` listener and the
// plugin's `audioEnded` event) with the id of the clip that finished. Exported
// so a test can drive the boundary without owning a media element.
export function handleSegmentAudioEnded(endedAudioId) {
  const session = activeSegmentSession();
  if (!session || session.phase !== "playing") return;
  // The session's own record, not module state: a stale end from another clip
  // must never chain this session forward.
  if (!endedAudioId || !ownsThePlayer(session, endedAudioId)) return;
  clearSegmentEndWatchdog(session);
  clearSegmentProgressPoll(session);
  session.nextIndex = session.playingIndex + 1;
  // This segment has been played; its bytes are never needed again.
  session.blobs.delete(session.playingIndex);
  session.playingIndex = null;
  session.playingAudioId = null;
  setSegmentPhase(session, "waiting");
  advanceSegmentSession(session);
}

// The single entry point the chat screen uses. `segments` is the ordered list
// of segment audio ids known so far, index-addressed the same way the server
// writes them, so re-offering a longer list is how a late segment arrives.
// Creating the session and feeding it later segments are the same call because
// the client cannot tell the two apart: both are just a `segments` write.
export function offerSegments(message, segments, options = {}) {
  const dialogId = message && message.dialogId;
  const messageKey = message && message.messageKey;
  if (!dialogId || !messageKey) return null;
  const ordered = Array.isArray(segments) ? segments : [];
  if (!ordered.length) return null;

  const key = segmentSessionKey(dialogId, messageKey);
  let session = segmentSessions.get(key);
  if (!session) {
    session = {
      key,
      dialogId,
      messageKey,
      segments: [],
      blobs: new Map(),
      // The first segment this session has not played yet. Only the
      // natural-end handler advances it.
      nextIndex: 0,
      playingIndex: null,
      playingAudioId: null,
      phase: "waiting",
      // Why the session ended, from the closed set described above, or null
      // while it is live. Written only by endSegmentSession, once.
      endReason: null,
      // Whether audible playback progress was ever observed for this reply.
      // Written only by noteAudibleProgress. Never set because play()
      // resolved: that happens before any sound, and a clip can still fail
      // before making one.
      heard: false,
      audioStartStamped: false,
      // Whether the learner asked for quiet (a record tap, a navigation) at
      // any point in this session's life, including after it ended. Written
      // only by pause() and at birth; see pause() for why it is separate from
      // the end reason.
      quietRequested: false,
      // The stitched file, recorded only when the final attach found this
      // session still live and mounted the player paused; see
      // playStitchedFallback.
      fallbackAudioId: null,
      fallbackStarted: false,
      // Set by the final attach: the server has stitched the reply, so the
      // segment list this session holds is now complete.
      finalAttachSeen: false,
      // Set when the chat drops this session. The object can still be held by
      // an in-flight fetch after the map entry is gone, and a dead session must
      // not start audio on whatever screen the learner moved to.
      retired: false,
      // Bounds the wait for a natural-end event once a segment starts, and the
      // wait for a segment that has not been written; see the watchdogs.
      endWatchdog: null,
      // Whether that watchdog was armed after the clip started, which is the
      // only kind that depends on the rate and that a rate change recomputes.
      endWatchdogAfterStart: false,
      // Set by the chat for a tutor message in a lesson or a freestyle chat:
      // this reply's segments, and its stitched fallback, play at the speed
      // chip's rate. Every other session plays at 1x.
      followsSpeedChip: false,
      // The iOS audible-progress poll; see watchNativeAudibleProgress.
      progressPoll: null,
      fetchSegment: null,
      lagContext: { dialogId, messageKey },
      // One settled-or-pending marker per segment fetch this session started,
      // and whether the stitched clip's prefetch has begun; see
      // prefetchStitchedClip.
      segmentFetches: [],
      stitchedPrefetchStarted: false,
      prefetchStopper: null,
      fallbackStopper: null,
      // Set by a phrase tap made while this session existed; its stitched
      // fallback then never autoplays. See playSlice.
      phraseTappedSince: false,
    };
    segmentSessions.set(key, session);
    setSegmentPhase(session, "waiting");
    // A stop request that landed AFTER this reply's text was already on screen
    // was aimed at this reply: the learner tapped record, or navigated, while
    // waiting for its audio. Ending it at birth is what stops the tutor talking
    // into that recording, and the stitched player is their way back to it.
    const renderedAt = options.renderedAt;
    if (typeof renderedAt === "number" && lastStopRequestAt >= renderedAt) {
      session.quietRequested = true;
      endSegmentSession(session, "silencedByLearner");
    }
  }
  if (typeof options.fetchSegment === "function") {
    session.fetchSegment = options.fetchSegment;
  }
  if (typeof options.followsSpeedChip === "boolean") {
    session.followsSpeedChip = options.followsSpeedChip;
  }

  // An ended session will never play another segment, so downloading one
  // would spend the learner's data on audio nobody will hear - and suspension
  // happens exactly when they are uploading a recording of their own. The ids
  // are still recorded, because the list is what a later reader describes, but
  // the bytes are not fetched.
  const ended = !!session.endReason;
  let added = false;
  ordered.forEach((audioId, index) => {
    if (!audioId || session.segments[index]) return;
    session.segments[index] = audioId;
    added = true;
    if (ended) return;
    // Warm the bytes now rather than at the boundary. The rejection is caught
    // here so an unused prefetch cannot surface as an unhandled rejection; the
    // cached promise still rejects for whoever actually awaits it.
    loadSegmentBlob(session, index).catch(() => {});
  });
  if (added && !ended) advanceSegmentSession(session);
  return session;
}

// Whether some clip other than this session's own is sounding, or about to.
// Autoplaying the stitched file over it would cut off whatever the learner is
// hearing now: another reply's session (live, playing or between sentences),
// a clip they tapped, or a clip still loading.
function anotherClipHoldsThePlayer(session) {
  const ownsClip = (audioId) => !!audioId && session.segments.includes(audioId);
  if (playbackState.isPlaying && !ownsClip(playbackState.currentAudioId)) return true;
  for (const request of inFlightPlays) {
    if (!isStalePlay(request) && !ownsClip(request.audioId)) return true;
  }
  for (const other of segmentSessions.values()) {
    if (other !== session && !other.endReason) return true;
  }
  return false;
}

// The stitched file has been attached to this message. That is the server
// saying the segment list is final - but NOT that playback is over: the stitch
// routinely finishes while the learner is still hearing the first sentence, and
// ending the session here would cut the reply off after one sentence. So a live
// session keeps chaining whatever it has already been given, and ends itself
// once it runs out.
//
// Returns whether the stitched file must mount PAUSED with its fetch deferred,
// which is true for every message this client holds a session for: the
// AudioPlayer never autoplays a session's stitched file itself. Whether the
// stitched file PLAYS on its own is decided here, in the service, because
// only the service can re-check the decision after the bytes arrive - a
// record tap or another clip during that fetch must still win, and the
// component's own autoplay would start regardless. It plays in exactly one
// case: the session ended because it FAILED, nothing of the reply was heard,
// the learner has not asked for quiet, and no other clip holds the player.
// Then the stitched file is the only way the learner gets the reply that would
// have played by itself before segments existed. Every other case stays
// paused:
//
//   - a live session is still delivering the reply itself (if it later fails
//     unheard, the same rule is applied then: playStitchedFallback);
//   - the learner tapped record or navigated after it failed: it would speak
//     into their recording;
//   - "completed", or anything heard: autoplaying would replay what they heard;
//   - "silencedByLearner": it would speak into their recording, or onto the
//     screen they moved to;
//   - "superseded": it would talk over the clip that displaced it;
//   - another clip holds the player: it would cut that clip off.
//
// With no session at all this returns false, so the caller keeps today's
// whole-file behavior (the component's own autoplay) unchanged.
export function completeSegmentSession(dialogId, messageKey, stitchedAudioId = null) {
  if (!dialogId || !messageKey) return false;
  const session = segmentSessions.get(segmentSessionKey(dialogId, messageKey));
  if (!session) return false;
  session.finalAttachSeen = true;
  session.fallbackAudioId = stitchedAudioId || null;
  if (
    !session.endReason &&
    session.phase === "waiting" &&
    !session.segments[session.nextIndex]
  ) {
    // Ending it "failed" here starts the stitched fallback by itself when the
    // rule allows it (endSegmentSession -> playStitchedFallback).
    endExhaustedSegmentSession(session);
  } else if (session.endReason) {
    // Already over before the attach: apply the rule now.
    playStitchedFallback(session);
    prefetchStitchedClip(session);
  }
  // A live session applies the rule later, if and when it fails.
  return true;
}

// How many segment blobs a session is still holding. Exported purely so the
// memory-release rule above is testable: sessions outlive their playback (the
// final attach needs to know how the reply ended), so "the record survives but
// the audio does not" is a property worth pinning rather than trusting.
export function countRetainedSegmentBlobs(dialogId, messageKey) {
  const session = segmentSessions.get(segmentSessionKey(dialogId, messageKey));
  return session ? session.blobs.size : 0;
}

// Chat teardown: leaving a chat drops every session it owned, so a stale one
// can never claim a later message's final attach, and an in-flight fetch that
// resolves afterwards cannot start audio on the screen the learner moved to.
export function endAllSegmentSessions() {
  // Speaking means a session holds the player, or a session's stitched
  // fallback is sounding or still loading. The fallback runs after its session
  // has ended, so the active session key alone would miss it.
  const fallbackIds = new Set();
  segmentSessions.forEach((session) => {
    if (session.fallbackStarted && session.fallbackAudioId) {
      fallbackIds.add(session.fallbackAudioId);
    }
  });
  let fallbackSpeaking =
    playbackState.isPlaying && fallbackIds.has(playbackState.currentAudioId);
  inFlightPlays.forEach((request) => {
    if (!isStalePlay(request) && fallbackIds.has(request.audioId)) {
      fallbackSpeaking = true;
    }
  });
  const wasSpeaking = activeSegmentSessionKey !== null || fallbackSpeaking;
  segmentSessions.forEach((session) => {
    session.retired = true;
    clearSegmentEndWatchdog(session);
    clearSegmentProgressPoll(session);
    if (session.prefetchStopper) session.prefetchStopper.abort();
    if (session.fallbackStopper) session.fallbackStopper.abort();
  });
  segmentSessions.clear();
  Object.keys(segmentPlaybackState.phases).forEach((key) => {
    delete segmentPlaybackState.phases[key];
  });
  activeSegmentSessionKey = null;
  // Dropping the records does not silence the clip already in the shared
  // player. Navigation is covered because router.afterEach pauses, but this
  // also runs from the dialogId watcher, which is a prop change with no route
  // transition - and there Arti would audibly finish a sentence from the chat
  // the learner just left, on top of the one they just opened.
  if (wasSpeaking) pause();
}

// ---------- The speed chip ----------
//
// A change to the effective rate (the chip, or the flag turning off) reaches a
// clip that is already sounding, but only one that follows the chip. On the
// web the element's rates change in place. On iOS the plugin's setRate changes
// the player mid-clip, and the clock snapshots its media time and re-anchors
// at the new rate so the position stays continuous; a clip that is paused
// takes the new rate from the resume instead. Synchronous, so the rate and the
// segment watchdog move in the same tick as the tap.
function applyChipRateChange(nextRate) {
  effectiveRateEpoch++;
  if (currentClip.followsChip && playbackState.currentAudioId) {
    if (useNative) {
      if (nativeRateSupported) {
        const previousRate = currentClip.rate;
        currentClip.rate = nextRate;
        if (playbackState.isPlaying) {
          const mediaTime = nativeClockMediaTime();
          playbackState.currentTime = mediaTime;
          anchorNativeClock(mediaTime, nextRate);
          setNativeRate(nextRate, previousRate);
        }
      }
    } else {
      applyElementRate(nextRate);
      currentClip.rate = nextRate;
    }
  }
  rearmSegmentWatchdogForRate();
  applySliceRateChange(nextRate);
}

watch(effectivePlaybackRate, applyChipRateChange, { flush: "sync" });

// Seconds listened at each rate. One event per stretch of tutor audio that
// played at one rate: a stretch starts when a chip-following clip starts
// sounding while the chip is available, and it ends at a pause, the clip's end,
// a rate change, the flag turning off, or another clip taking the player.
// Wall-clock seconds, because that is what the lesson's time allowance counts.
const playbackEvents = usePlaybackEvents();
let listeningStretch = null;

const listeningRate = computed(() =>
  playbackState.isPlaying && currentClip.followsChip && playbackRateAvailable.value
    ? currentClip.rate
    : null
);

watch(
  listeningRate,
  (nextRate) => {
    const now = performance.now();
    if (listeningStretch) {
      playbackEvents.trackSecondsListened({
        rate: listeningStretch.rate,
        seconds: (now - listeningStretch.startedAt) / 1000,
      });
      listeningStretch = null;
    }
    if (nextRate !== null) listeningStretch = { rate: nextRate, startedAt: now };
  },
  { flush: "sync" }
);
